import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import moment from 'moment';
import axiosInstance from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';

const UpcomingPayments = () => {
    const { companyId } = useParams();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [payments, setPayments] = useState([]);
    const [summary, setSummary] = useState({
        totalReceivables: 0,
        totalPayables: 0,
        netCashFlow: 0,
        overdueCount: 0,
        urgentCount: 0
    });

    useEffect(() => {
        fetchUpcomingPayments();
    }, [companyId]);

    const fetchUpcomingPayments = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await axiosInstance.get(
                getApiUrl(`${companyId}/dashboard/upcoming-payments`),
                {
                    params: { limit: 8 },
                    withCredentials: true
                }
            );

            setPayments(response.data.payments || []);
            setSummary(response.data.summary || {});
        } catch (error) {
            console.error('Error fetching upcoming payments:', error);
            setError('Kunde inte ladda betalningsdata');
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('sv-SE', {
            style: 'currency',
            currency: 'SEK',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(Math.abs(amount));
    };

    const getDaysUntilDue = (dueDate) => {
        const today = moment();
        const due = moment(dueDate);
        return due.diff(today, 'days');
    };

    const getPaymentIcon = (type, daysUntilDue) => {
        const isOverdue = daysUntilDue < 0;
        const isUrgent = daysUntilDue >= 0 && daysUntilDue <= 3;

        if (type === 'invoice') {
            return (
                <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '12px',
                    background: isOverdue ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' :
                        isUrgent ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' :
                            'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: isOverdue ? '0 4px 12px rgba(239, 68, 68, 0.3)' :
                        isUrgent ? '0 4px 12px rgba(245, 158, 11, 0.3)' :
                            '0 4px 12px rgba(59, 130, 246, 0.3)',
                    transition: 'all 0.3s ease'
                }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="text-white">
                        <path d="M14 2H6C5.44772 2 5 2.44772 5 3V21C5 21.5523 5.44772 22 6 22H18C18.5523 22 19 21.5523 19 21V7L14 2Z"
                            stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M16 13H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M16 17H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M10 9H9H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </div>
            );
        } else {
            return (
                <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '12px',
                    background: isOverdue ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' :
                        isUrgent ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' :
                            'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: isOverdue ? '0 4px 12px rgba(239, 68, 68, 0.3)' :
                        isUrgent ? '0 4px 12px rgba(245, 158, 11, 0.3)' :
                            '0 4px 12px rgba(239, 68, 68, 0.3)',
                    transition: 'all 0.3s ease'
                }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="text-white">
                        <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </div>
            );
        }
    };

    const getUrgencyBadge = (daysUntilDue, status) => {
        if (status === 'overdue' || daysUntilDue < 0) {
            return (
                <span style={{
                    fontSize: '0.75rem',
                    fontWeight: '500',
                    padding: '0.25rem 0.5rem',
                    borderRadius: '6px',
                    background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                    color: 'white',
                    boxShadow: '0 2px 4px rgba(239, 68, 68, 0.2)'
                }}>
                    Försenad
                </span>
            );
        } else if (daysUntilDue <= 3) {
            return (
                <span style={{
                    fontSize: '0.75rem',
                    fontWeight: '500',
                    padding: '0.25rem 0.5rem',
                    borderRadius: '6px',
                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    color: 'white',
                    boxShadow: '0 2px 4px rgba(245, 158, 11, 0.2)'
                }}>
                    {daysUntilDue === 0 ? 'Idag' : `${daysUntilDue} dag${daysUntilDue > 1 ? 'ar' : ''}`}
                </span>
            );
        } else if (daysUntilDue <= 7) {
            return (
                <span style={{
                    fontSize: '0.75rem',
                    fontWeight: '500',
                    padding: '0.25rem 0.5rem',
                    borderRadius: '6px',
                    background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
                    color: 'white',
                    boxShadow: '0 2px 4px rgba(6, 182, 212, 0.2)'
                }}>
                    {daysUntilDue} dagar
                </span>
            );
        } else {
            return (
                <span style={{
                    fontSize: '0.75rem',
                    fontWeight: '500',
                    padding: '0.25rem 0.5rem',
                    borderRadius: '6px',
                    background: '#f1f5f9',
                    color: '#64748b',
                    border: '1px solid #e2e8f0'
                }}>
                    {daysUntilDue} dagar
                </span>
            );
        }
    };

    if (loading) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '300px'
            }}>
                <div>Laddar betalningsdata...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{
                textAlign: 'center',
                padding: '2rem',
                color: '#64748b'
            }}>
                <p>{error}</p>
                <button
                    onClick={fetchUpcomingPayments}
                    style={{
                        padding: '0.5rem 1rem',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        background: 'white',
                        color: '#374151',
                        cursor: 'pointer'
                    }}
                >
                    Försök igen
                </button>
            </div>
        );
    }

    return (
        <div className="upcoming-payments">
            {/* Summary Section - keep as is */}
            {summary.netCashFlow !== 0 && (
                <div style={{
                    background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                    borderRadius: '12px',
                    padding: '1rem',
                    marginBottom: '1rem',
                    border: '1px solid #e2e8f0'
                }}>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                        gap: '1rem',
                        fontSize: '0.75rem'
                    }}>
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ color: '#64748b', marginBottom: '0.25rem' }}>Förväntat inflöde</div>
                            <div style={{ fontWeight: '600', color: '#10b981' }}>
                                {formatCurrency(summary.totalReceivables)}
                            </div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ color: '#64748b', marginBottom: '0.25rem' }}>Förväntat utflöde</div>
                            <div style={{ fontWeight: '600', color: '#ef4444' }}>
                                {formatCurrency(summary.totalPayables)}
                            </div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ color: '#64748b', marginBottom: '0.25rem' }}>Netto kassaflöde</div>
                            <div style={{
                                fontWeight: '600',
                                color: summary.netCashFlow >= 0 ? '#10b981' : '#ef4444'
                            }}>
                                {summary.netCashFlow >= 0 ? '+' : ''}{formatCurrency(summary.netCashFlow)}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {payments.length === 0 ? (
                <div style={{
                    textAlign: 'center',
                    padding: '3rem 1rem',
                    background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                    borderRadius: '16px',
                    border: '1px solid #e2e8f0'
                }}>
                    <div style={{
                        width: '64px',
                        height: '64px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 1rem'
                    }}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="text-slate-400">
                            <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" />
                        </svg>
                    </div>
                    <p style={{ color: '#64748b', fontSize: '0.875rem', margin: 0 }}>
                        Inga kommande betalningar
                    </p>
                </div>
            ) : (
                <div className="payment-list" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {payments.map((payment) => {
                        const daysUntilDue = getDaysUntilDue(payment.dueDate);

                        return (
                            <div
                                key={payment.id}
                                style={{
                                    background: 'white',
                                    borderRadius: '16px',
                                    padding: '1.25rem',
                                    border: '1px solid #e2e8f0', // Consistent border for all payments
                                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)', // Consistent shadow for all payments
                                    transition: 'all 0.3s ease',
                                    cursor: 'pointer'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'translateY(-2px)';
                                    e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.08)'; // Consistent hover shadow
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.04)'; // Back to consistent shadow
                                }}
                            >
                                <div className="d-flex align-items-center justify-content-between">
                                    <div className="d-flex align-items-center">
                                        {getPaymentIcon(payment.type, daysUntilDue)}
                                        <div className="ms-3">
                                            <h6 style={{
                                                margin: '0 0 0.25rem 0',
                                                fontSize: '0.875rem',
                                                fontWeight: '600',
                                                color: '#1e293b',
                                                lineHeight: '1.4'
                                            }}>
                                                {payment.description}
                                            </h6>
                                            <div style={{
                                                fontSize: '0.75rem',
                                                color: '#64748b',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.5rem'
                                            }}>
                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" className="text-slate-400">
                                                    <path d="M8 2V6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                    <path d="M16 2V6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" stroke="currentColor" strokeWidth="2" />
                                                    <path d="M3 10H21" stroke="currentColor" strokeWidth="2" />
                                                </svg>
                                                {moment(payment.dueDate).format('D MMM YYYY')}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="text-end">
                                        <div style={{
                                            fontSize: '0.875rem',
                                            fontWeight: '700',
                                            color: payment.amount > 0 ? '#10b981' : '#ef4444',
                                            marginBottom: '0.5rem',
                                            letterSpacing: '-0.025em'
                                        }}>
                                            {payment.amount > 0 ? '+' : ''}{formatCurrency(payment.amount)}
                                        </div>
                                        {getUrgencyBadge(daysUntilDue, payment.status)}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            <div className="text-center mt-4">
                <Link
                    to={`/${companyId}/invoices`}
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.75rem 1.5rem',
                        background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                        color: 'white',
                        textDecoration: 'none',
                        borderRadius: '12px',
                        fontSize: '0.875rem',
                        fontWeight: '500',
                        boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
                        transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 8px 20px rgba(59, 130, 246, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
                    }}
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    Hantera betalningar
                </Link>
            </div>
        </div>
    );
};

export default UpcomingPayments;



// import React, { useState, useEffect } from 'react';
// import { Link, useParams } from 'react-router-dom';
// import { Badge } from 'react-bootstrap';
// import moment from 'moment';
// import axiosInstance from '../../../../services/AxiosInstance';
// import { getApiUrl } from '../../../../utils/ApiUtils';

// const UpcomingPayments = () => {
//     const { companyId } = useParams();
//     const [payments, setPayments] = useState([
//         {
//             id: '1',
//             type: 'invoice',
//             description: 'Faktura #1114 - Ework Group AB',
//             amount: 31250,
//             dueDate: '2025-06-15',
//             status: 'pending'
//         },
//         {
//             id: '2',
//             type: 'bill',
//             description: 'Hyra kontor - Fastighets AB',
//             amount: -12000,
//             dueDate: '2025-06-20',
//             status: 'pending'
//         },
//         {
//             id: '3',
//             type: 'invoice',
//             description: 'Faktura #1115 - ABC Consulting',
//             amount: 25000,
//             dueDate: '2025-06-25',
//             status: 'pending'
//         },
//         {
//             id: '4',
//             type: 'bill',
//             description: 'Skattebetalning Q2',
//             amount: -45000,
//             dueDate: '2025-06-30',
//             status: 'pending'
//         }
//     ]);

//     useEffect(() => {
//         fetchUpcomingPayments();
//     }, [companyId]);

//     const fetchUpcomingPayments = async () => {
//         try {
//             // Replace with actual API call
//             // const response = await axiosInstance.get(getApiUrl(`${companyId}/dashboard/upcoming-payments`));
//             // setPayments(response.data);
//         } catch (error) {
//             console.error('Error fetching upcoming payments:', error);
//         }
//     };

//     const formatCurrency = (amount) => {
//         return new Intl.NumberFormat('sv-SE', {
//             style: 'currency',
//             currency: 'SEK',
//             minimumFractionDigits: 0,
//             maximumFractionDigits: 0
//         }).format(Math.abs(amount));
//     };

//     const getDaysUntilDue = (dueDate) => {
//         const today = moment();
//         const due = moment(dueDate);
//         return due.diff(today, 'days');
//     };

//     const getPaymentIcon = (type, daysUntilDue) => {
//         const isUrgent = daysUntilDue <= 3;

//         if (type === 'invoice') {
//             return (
//                 <div style={{
//                     width: '48px',
//                     height: '48px',
//                     borderRadius: '12px',
//                     background: isUrgent ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' : 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
//                     display: 'flex',
//                     alignItems: 'center',
//                     justifyContent: 'center',
//                     boxShadow: isUrgent ? '0 4px 12px rgba(16, 185, 129, 0.3)' : '0 4px 12px rgba(59, 130, 246, 0.3)',
//                     transition: 'all 0.3s ease'
//                 }}>
//                     <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="text-white">
//                         <path d="M14 2H6C5.44772 2 5 2.44772 5 3V21C5 21.5523 5.44772 22 6 22H18C18.5523 22 19 21.5523 19 21V7L14 2Z"
//                             stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
//                         <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
//                         <path d="M16 13H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
//                         <path d="M16 17H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
//                         <path d="M10 9H9H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
//                     </svg>
//                 </div>
//             );
//         } else {
//             return (
//                 <div style={{
//                     width: '48px',
//                     height: '48px',
//                     borderRadius: '12px',
//                     background: isUrgent ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
//                     display: 'flex',
//                     alignItems: 'center',
//                     justifyContent: 'center',
//                     boxShadow: isUrgent ? '0 4px 12px rgba(245, 158, 11, 0.3)' : '0 4px 12px rgba(239, 68, 68, 0.3)',
//                     transition: 'all 0.3s ease'
//                 }}>
//                     <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="text-white">
//                         <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
//                         <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
//                         <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
//                     </svg>
//                 </div>
//             );
//         }
//     };

//     const getUrgencyBadge = (daysUntilDue) => {
//         if (daysUntilDue < 0) {
//             return (
//                 <span style={{
//                     fontSize: '0.75rem',
//                     fontWeight: '500',
//                     padding: '0.25rem 0.5rem',
//                     borderRadius: '6px',
//                     background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
//                     color: 'white',
//                     boxShadow: '0 2px 4px rgba(239, 68, 68, 0.2)'
//                 }}>
//                     Försenad
//                 </span>
//             );
//         } else if (daysUntilDue <= 3) {
//             return (
//                 <span style={{
//                     fontSize: '0.75rem',
//                     fontWeight: '500',
//                     padding: '0.25rem 0.5rem',
//                     borderRadius: '6px',
//                     background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
//                     color: 'white',
//                     boxShadow: '0 2px 4px rgba(245, 158, 11, 0.2)'
//                 }}>
//                     {daysUntilDue === 0 ? 'Idag' : `${daysUntilDue} dag${daysUntilDue > 1 ? 'ar' : ''}`}
//                 </span>
//             );
//         } else if (daysUntilDue <= 7) {
//             return (
//                 <span style={{
//                     fontSize: '0.75rem',
//                     fontWeight: '500',
//                     padding: '0.25rem 0.5rem',
//                     borderRadius: '6px',
//                     background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
//                     color: 'white',
//                     boxShadow: '0 2px 4px rgba(6, 182, 212, 0.2)'
//                 }}>
//                     {daysUntilDue} dagar
//                 </span>
//             );
//         } else {
//             return (
//                 <span style={{
//                     fontSize: '0.75rem',
//                     fontWeight: '500',
//                     padding: '0.25rem 0.5rem',
//                     borderRadius: '6px',
//                     background: '#f1f5f9',
//                     color: '#64748b',
//                     border: '1px solid #e2e8f0'
//                 }}>
//                     {daysUntilDue} dagar
//                 </span>
//             );
//         }
//     };

//     return (
//         <div className="upcoming-payments">
//             {payments.length === 0 ? (
//                 <div style={{
//                     textAlign: 'center',
//                     padding: '3rem 1rem',
//                     background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
//                     borderRadius: '16px',
//                     border: '1px solid #e2e8f0'
//                 }}>
//                     <div style={{
//                         width: '64px',
//                         height: '64px',
//                         borderRadius: '50%',
//                         background: 'linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%)',
//                         display: 'flex',
//                         alignItems: 'center',
//                         justifyContent: 'center',
//                         margin: '0 auto 1rem'
//                     }}>
//                         <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="text-slate-400">
//                             <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
//                             <path d="M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" />
//                         </svg>
//                     </div>
//                     <p style={{ color: '#64748b', fontSize: '0.875rem', margin: 0 }}>
//                         Inga kommande betalningar
//                     </p>
//                 </div>
//             ) : (
//                 <div className="payment-list" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
//                     {payments.map((payment) => {
//                         const daysUntilDue = getDaysUntilDue(payment.dueDate);
//                         const isUrgent = daysUntilDue <= 3;

//                         return (
//                             <div
//                                 key={payment.id}
//                                 style={{
//                                     background: 'white',
//                                     borderRadius: '16px',
//                                     padding: '1.25rem',
//                                     border: isUrgent ? '2px solid #f59e0b' : '1px solid #e2e8f0',
//                                     boxShadow: isUrgent ? '0 8px 25px rgba(245, 158, 11, 0.15)' : '0 2px 8px rgba(0, 0, 0, 0.04)',
//                                     transition: 'all 0.3s ease',
//                                     cursor: 'pointer',
//                                     position: 'relative',
//                                     overflow: 'hidden'
//                                 }}
//                                 onMouseEnter={(e) => {
//                                     e.currentTarget.style.transform = 'translateY(-2px)';
//                                     e.currentTarget.style.boxShadow = isUrgent ? '0 12px 35px rgba(245, 158, 11, 0.25)' : '0 8px 25px rgba(0, 0, 0, 0.08)';
//                                 }}
//                                 onMouseLeave={(e) => {
//                                     e.currentTarget.style.transform = 'translateY(0)';
//                                     e.currentTarget.style.boxShadow = isUrgent ? '0 8px 25px rgba(245, 158, 11, 0.15)' : '0 2px 8px rgba(0, 0, 0, 0.04)';
//                                 }}
//                             >
//                                 {isUrgent && (
//                                     <div style={{
//                                         position: 'absolute',
//                                         top: 0,
//                                         left: 0,
//                                         right: 0,
//                                         height: '3px',
//                                         background: 'linear-gradient(90deg, #f59e0b 0%, #d97706 100%)'
//                                     }} />
//                                 )}

//                                 <div className="d-flex align-items-center justify-content-between">
//                                     <div className="d-flex align-items-center">
//                                         {getPaymentIcon(payment.type, daysUntilDue)}
//                                         <div className="ms-3">
//                                             <h6 style={{
//                                                 margin: '0 0 0.25rem 0',
//                                                 fontSize: '0.875rem',
//                                                 fontWeight: '600',
//                                                 color: '#1e293b',
//                                                 lineHeight: '1.4'
//                                             }}>
//                                                 {payment.description}
//                                             </h6>
//                                             <div style={{
//                                                 fontSize: '0.75rem',
//                                                 color: '#64748b',
//                                                 display: 'flex',
//                                                 alignItems: 'center',
//                                                 gap: '0.5rem'
//                                             }}>
//                                                 <svg width="12" height="12" viewBox="0 0 24 24" fill="none" className="text-slate-400">
//                                                     <path d="M8 2V6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
//                                                     <path d="M16 2V6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
//                                                     <rect x="3" y="4" width="18" height="18" rx="2" ry="2" stroke="currentColor" strokeWidth="2" />
//                                                     <path d="M3 10H21" stroke="currentColor" strokeWidth="2" />
//                                                 </svg>
//                                                 {moment(payment.dueDate).format('D MMM YYYY')}
//                                             </div>
//                                         </div>
//                                     </div>

//                                     <div className="text-end">
//                                         <div style={{
//                                             fontSize: '0.875rem',
//                                             fontWeight: '700',
//                                             color: payment.amount > 0 ? '#10b981' : '#ef4444',
//                                             marginBottom: '0.5rem',
//                                             letterSpacing: '-0.025em'
//                                         }}>
//                                             {payment.amount > 0 ? '+' : ''}{formatCurrency(payment.amount)}
//                                         </div>
//                                         {getUrgencyBadge(daysUntilDue)}
//                                     </div>
//                                 </div>
//                             </div>
//                         );
//                     })}
//                 </div>
//             )}

//             <div className="text-center mt-4">
//                 <Link
//                     to={`/${companyId}/invoices`}
//                     style={{
//                         display: 'inline-flex',
//                         alignItems: 'center',
//                         gap: '0.5rem',
//                         padding: '0.75rem 1.5rem',
//                         background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
//                         color: 'white',
//                         textDecoration: 'none',
//                         borderRadius: '12px',
//                         fontSize: '0.875rem',
//                         fontWeight: '500',
//                         boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
//                         transition: 'all 0.3s ease'
//                     }}
//                     onMouseEnter={(e) => {
//                         e.currentTarget.style.transform = 'translateY(-2px)';
//                         e.currentTarget.style.boxShadow = '0 8px 20px rgba(59, 130, 246, 0.4)';
//                     }}
//                     onMouseLeave={(e) => {
//                         e.currentTarget.style.transform = 'translateY(0)';
//                         e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
//                     }}
//                 >
//                     <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
//                         <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
//                         <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
//                         <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
//                     </svg>
//                     Hantera betalningar
//                 </Link>
//             </div>
//         </div>
//     );
// };

// export default UpcomingPayments;